import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useMutation } from '@parkchirp/api-client';
import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react';

import { UPDATE_INVOICE_DUE_DATE } from '../../../mutations';
import { SnackBarProps } from '../../../types';
import { dayjs } from '../../../utils';
import { EditorModal } from '../../modals';

type EditDueDateModalProps = {
	invoiceId: string;
	originalDueDate: string;
	handleClose: (shouldRefetch: boolean) => void;
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackBarProps> | null>>;
};

const EditDueDateModal: FC<EditDueDateModalProps> = ({ invoiceId, originalDueDate, handleClose, setShowSnackbar }) => {
	const [dueDate, setDueDate] = useState(originalDueDate);
	const [updateInvoiceDueDate, { loading }] = useMutation(UPDATE_INVOICE_DUE_DATE);

	const theme = useTheme();

	const handleSave = useCallback(async () => {
		await updateInvoiceDueDate({
			context: { apiVersion: 'v2' },
			variables: {
				invoiceId,
				dueDate,
			},
			onCompleted: () => {
				setShowSnackbar({
					message: 'Successfully saved invoice due date',
					severity: 'success',
				});

				handleClose(true);
			},
			onError: (e) => {
				console.log(e);
				setShowSnackbar({
					message: `Unable to save invoice due date, please try again`,
					severity: 'error',
					autoHideDuration: null,
				});
			},
		});
	}, [dueDate, handleClose, invoiceId, setShowSnackbar, updateInvoiceDueDate]);

	return (
		<EditorModal
			headerText='Edit Invoice Due Date'
			theme={theme}
			handleClose={() => handleClose(false)}
			handleSave={handleSave}
			loading={loading}
			modalWidth='500px'
		>
			<Box
				style={{
					minHeight: '150px',
					minWidth: '10px',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker
						label='Due Date'
						value={dayjs.utc(dueDate)}
						onChange={(date) => {
							setDueDate(date.format('YYYY-MM-DD'));
						}}
						sx={{ width: '50%' }}
						slotProps={{
							textField: {
								inputProps: { 'data-cy': 'due-date-date-picker' },
							},
						}}
					/>
				</LocalizationProvider>
			</Box>
		</EditorModal>
	);
};

export default EditDueDateModal;
